import React, { useState, useEffect, useMemo } from "react";
import { withStyles, WithStyles } from "@material-ui/core";
import { styles } from "./PasswordValidator.styles";
import { Rule } from "./Rule";

export type RuleNames = "minLength" | "lowercase" | "number" | "symbol" | "uppercase" | "match";

export interface IPasswordValidatorProps extends WithStyles<typeof styles> {
    rules: Array<RuleNames>;
    password: string;
    passwordConfirm?: string;
    minLength?: number;
    onChange?: (isValid: boolean) => any;
    onlyInvalidRules?: boolean;
}
const PasswordValidatorPure: React.FunctionComponent<IPasswordValidatorProps> = ({
    rules,
    password,
    passwordConfirm,
    minLength,
    onChange,
    onlyInvalidRules,
    classes
}) => {
    const [isValid, setIsValid] = useState(false);

    const getRules = (password, passwordConfirm, minLength) => {
        return {
            minLength: {
                valid: password.length >= minLength,
                message: `Password has at least ${minLength} characters.`
            },
            symbol: {
                valid: /[~`!#$%^&*@[\]\\';,/{}|\\":<>?.)(_]/g.test(password),
                message: "Password has special characters."
            },
            number: {
                valid: /\d/g.test(password),
                message: "Password has a number."
            },
            uppercase: {
                valid: /.*[A-Z].*/g.test(password),
                message: "Password has a uppercase letter."
            },
            match: {
                valid: password.length > 0 && password === passwordConfirm,
                message: "Passwords match."
            },
            lowercase: {
                valid: /.*[a-z].*/g.test(password),
                message: "Password has a lowercase letter."
            }
        };
    };
    const ruleDefinitions: {
        [key in RuleNames]: { valid: boolean; message: string };
    } = useMemo(() => getRules(password, passwordConfirm, minLength), [password, passwordConfirm, minLength]);

    const enabledRules = rules.filter(rule => Boolean(ruleDefinitions[rule]));
    const rulesToRender = onlyInvalidRules ? enabledRules.filter(r => !ruleDefinitions[r]?.valid) : enabledRules;

    useEffect(() => {
        const result = enabledRules.every(rule => ruleDefinitions[rule].valid);
        if (result !== isValid) {
            setIsValid(result);
            onChange(result);
        }
    }, [password, passwordConfirm, enabledRules, ruleDefinitions, isValid, onChange]);

    return (
        <ul className={classes.list}>
            {rulesToRender?.map(rule => {
                const { message, valid } = ruleDefinitions[rule];
                return <Rule key={rule} valid={valid} text={message} />;
            })}
        </ul>
    );
};

PasswordValidatorPure.defaultProps = {
    minLength: 8
};

export const PasswordValidator = withStyles(styles)(PasswordValidatorPure);
